import { useDrag } from '@use-gesture/react';
import type { CSSProperties, ReactNode } from 'react';

import {
  CarouselHolder,
  CarouselSlide,
  CarouselSlider,
  CarouselSliderHolder,
  currentPageVar,
  gutterVar,
  indexVar,
  nthIndexLgVar,
  nthIndexMdVar,
  nthIndexSmVar,
  nthIndexXsVar,
  slidePaddingVar,
  slidesPerPageLgVar,
  slidesPerPageMdVar,
  slidesPerPageSmVar,
  slidesPerPageXsVar,
} from './Carousel.styled';
import type { CarouselState } from './CarouselState';

type BaseProps = {
  id?: string;
  state: CarouselState;
  slides: ReactNode[];
  navigation?: ReactNode;
  gutter?: number;
  slidePadding?: number;
  qaId: string;
};

type Props = BaseProps &
  (
    | { autoHeight?: false; height: number }
    | { autoHeight: true; height?: never }
  );

export function Carousel(props: Props) {
  const {
    id,
    state,
    qaId,
    slides,
    gutter = 8,
    slidePadding = 0,
    navigation,
  } = props;
  // NOTE: Alternatively we could update the types further to avoid lint toggle,
  // however this would require all of the consumers of Carousel.
  const autoHeight = 'autoHeight' in props && props.autoHeight;
  const height = 'height' in props ? props.height : undefined;

  const {
    currentPage,
    responsiveSlidesPerPage,
    goToPreviousPage,
    goToNextPage,
  } = state;

  const swipeProps = useDrag(
    ({ direction: [directionX], distance: [distanceX], cancel }) => {
      // Swipe to the left
      if (directionX === -1 && distanceX > 10) {
        goToNextPage();
        cancel();
      }
      // Swipe to the right
      else if (directionX === 1 && distanceX > 10) {
        goToPreviousPage();
        cancel();
      }
    },
  )();

  return (
    <CarouselHolder id={id} data-qa-id={qaId}>
      <div
        style={{ touchAction: 'none' }}
        // oxlint-disable-next-line react/jsx-props-no-spreading
        {...swipeProps}
      >
        <CarouselSliderHolder
          $autoHeight={autoHeight}
          style={autoHeight ? undefined : { height }}
        >
          <CarouselSlider
            $autoHeight={autoHeight}
            data-qa-slide-count={slides.length}
            data-qa-id={`${qaId}-slider`}
            style={
              {
                [currentPageVar]: currentPage,
                [gutterVar]: `${gutter}px`,
                [slidePaddingVar]: `${slidePadding}px`,
                [slidesPerPageXsVar]: responsiveSlidesPerPage.xs,
                [slidesPerPageSmVar]: responsiveSlidesPerPage.sm,
                [slidesPerPageMdVar]: responsiveSlidesPerPage.md,
                [slidesPerPageLgVar]: responsiveSlidesPerPage.lg,
              } as CSSProperties
            }
          >
            {slides.map((slide, index) => (
              <CarouselSlide
                $autoHeight={autoHeight}
                key={index}
                data-qa-id={`${qaId}-slide-${index}`}
                style={
                  autoHeight
                    ? undefined
                    : ({
                        [indexVar]: index,
                        [nthIndexXsVar]: index % responsiveSlidesPerPage.xs,
                        [nthIndexSmVar]: index % responsiveSlidesPerPage.sm,
                        [nthIndexMdVar]: index % responsiveSlidesPerPage.md,
                        [nthIndexLgVar]: index % responsiveSlidesPerPage.lg,
                      } as CSSProperties)
                }
              >
                {slide}
              </CarouselSlide>
            ))}
          </CarouselSlider>
        </CarouselSliderHolder>
      </div>

      {navigation}
    </CarouselHolder>
  );
}
